import {
  Channels,
  CHANNEL_LINEUP_VIEW,
  PACKAGE_INDICATORS
  // Channel
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  FALLBACK_CHNL_ICON_URL
} from "./fallbackData";
import { resolveRelativeLogoSrc } from "lib/helper/channel-logo-url";
import { isChannelsServiceLocal, isServiceLocalInPackage } from "lib/service/channels/get-local-national-dma-eligible-channels";

// Generated by Copilot- Start - 2026-03-13 23:09:54
export function deepClone<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }

  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}
// Generated by Copilot- End - 2026-03-13 23:09:54

export function getSortForViewMode(mode) {
  switch (mode) {
    case CHANNEL_LINEUP_VIEW.HD:
      return Channels.sortByHd;
    case CHANNEL_LINEUP_VIEW.CATEGORY:
      return Channels.sortAlphabetically;
    case CHANNEL_LINEUP_VIEW.CHANNELS:
      return Channels.sortAlphabetically;
    case CHANNEL_LINEUP_VIEW.CHANNELNUMBER:
      return Channels.sortByChannelNumber;
  }
};

export function orderCategories(categories: string[], sortKeyArray: string[]) {
  const sorted = [];
  // eslint-disable-next-line no-plusplus
  for (let srtIx = 0; srtIx < sortKeyArray.length; srtIx++) {
    if (categories.includes(sortKeyArray[srtIx])) { //
      const ixMatch = categories.indexOf(sortKeyArray[srtIx]);
      const key = categories[ixMatch];
      sorted.push(key);
    }
  }
  return sorted.length > 0 ? sorted : categories;
}

export function getBillingCodeLabel(packageId) {
  if (packageId.includes('ATT_TV_')) {
    return packageId.split('ATT_TV_')[1];
  } else if (packageId.includes('DTV_')) {
    return packageId.split('DTV_')[1];
  }
  return 'NULL';
};

/**
 * @description Create a channel object from local/rsn API data. Aligns
 * the naming with the Channel class and National channel parameters.
 *
 * @param chnl channel object to parse, raw from feed minus chnlPackages adjustments for GP
 * @param localrsnUUID unique identifier for local/rsn channel
 * @param packages chnlPackages, if any/modified
 * @returns
 */
export const buildLocalRsnChannel = (
  chnl,
  localrsnUUID: number,
  packages: string[],
  indicators: any[]
) => {
  // Structural markers derived once from the raw API categoryId ("LOCAL"/"RSN",
  // case-insensitive — same normalization the chlupStore reads apply). Set on the
  // built lineup channel so the chlupStore display builders can detect LOCAL/RSN
  // via the flag instead of string-comparing the scalar `category`. The existing
  // `category` field is preserved untouched (Stage 1).
  // [WAT-11771] One guarded channel number for the row's identity strings AND its `chnlNum`
  // field, so the two can never disagree.
  const localRsnChnlNum = `${chnl?.chnlNum || ""}`;
  const normalizedCategory = String(chnl?.categoryId ?? "").toUpperCase();
  const isLocal = normalizedCategory === "LOCAL";
  const isRsn = normalizedCategory === "RSN";
  const c = {
    // [WAT-11771] See channel-lineup-parser: `uuid` is the display key the grid builders
    // overwrite per bucket; `matchId` is the feed identity they preserve, and it is what the
    // search matcher compares. Local/RSN rows are built client-side but flow into the same
    // builders, so they need both. Both use the SAME guarded number as the `chnlNum` field
    // below, so a missing feed number can no longer embed the literal "undefined" in an
    // identity string.
    uuid: `${localrsnUUID}-${chnl?.label}-${localRsnChnlNum}`,
    matchId: `${localrsnUUID}-${chnl?.label}-${localRsnChnlNum}`,
    type: "Included",
    name: chnl?.label,
    category: chnl?.categoryId,
    isLocal,
    isRsn,
    // WAT-11362: absolute-tolerant via shared helper (was an inline directv.com
    // prepend). `chnl.logo` is the zip/RSN feed logo today, but this guards it if
    // channels-service ever sources it from the absolute OV imageURL.
    icon: resolveRelativeLogoSrc(chnl?.logo) || FALLBACK_CHNL_ICON_URL,
    // WAT-11362: carry the server's OV decision to the render site.
    isOvLogo: Boolean(chnl?.isOvLogo),
    // [WAT-11771] Collapse a missing feed number to a FALSY "" — mirroring the proven
    // sibling in channel-lineup-parser.ts (`${c?.tunerPosition || ""}`). A bare template
    // literal stringified `undefined` into the truthy sentinel "undefined", which every
    // numberless local/RSN row then shared, so the search match's number comparison
    // treated them all as the same channel.
    chnlNum: localRsnChnlNum,
    isInclAny: true,
    isAddOnPromoAny: false,
    isStream: chnl?.isStream,
    isSatellite: chnl?.isSatellite,
    callSign: chnl?.callSign || chnl?.callLetter,
    displayText: chnl?.callSign, // for MultiChnlSearch
    chnlPackages: packages?.length ? packages : [],
    indicators,
    // WAT-11611: the store re-filters these rows by signature package; the marker tells it
    // to trust this station's own list instead of the CMS exclusions.
    ...(isChannelsServiceLocal(chnl) && {
      fromChannelsService: true,
      packageNames: chnl?.packageNames ?? []
    }),
    ...(isRsn && {
      mlb: chnl?.mlb,
      mls: chnl?.mls,
      nba: chnl?.nba,
      nhl: chnl?.nhl,
      wnba: chnl?.wnba,
      rsntier: chnl?.rsntier
    })
  };
  return c;
}

export const isChannelInGenre = (
  channel: any,
  genreLocalChannelMapping: any, genrePackKey: string,
  dma: string, nielsenDma: string,
  packageId?: string
) => {
  if (genrePackKey === "myhometeam") {
    const isMyHomeTeamEligible = channel?.packageNames?.includes?.("MYHOMETEAM"); // Note -> have to update this with the actual key that Q's team will provide
    return isMyHomeTeamEligible;
  }
  // A channels-service station (WAT-11611) is placed by its own package list, not the CMS mapping.
  if (isChannelsServiceLocal(channel)) {
    return isServiceLocalInPackage(channel, packageId);
  }
  // Local channels filter for rest of the genres
  const isLocalChannel = genreLocalChannelMapping?.find?.(mapChnl => mapChnl?.channel === channel?.network);
  const isLocalBelongToGenre = isLocalChannel ? ![undefined, null, ""]?.includes?.(isLocalChannel?.[genrePackKey]) : false;
  const isLocalChannelInDma = isLocalBelongToGenre && (
    isLocalChannel?.[genrePackKey]?.toLowerCase() === "all" ||
      isLocalChannel?.[genrePackKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.())
  );
  return isLocalChannel && isLocalBelongToGenre && isLocalChannelInDma;
};
export const isNationalForGenre = (
  channel: any,
  genrePackKey: string,
  dma: string,
  nielsenDma: string,
  genreNationalMapping: any
): boolean => {
  const nationalMapping = genreNationalMapping?.find?.(
    mapping => mapping?.channel === channel?.network
  );
  if (!nationalMapping) {
    return false;
  }
  const isLocalBelongToGenre = nationalMapping ? ![undefined, null, ""]?.includes?.(nationalMapping?.[genrePackKey]) : false;
  const isNationalDMA = isLocalBelongToGenre && (
    nationalMapping?.[genrePackKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.())
  );
  return isNationalDMA;
};

export function getMaxPackageDim(
  thElem: HTMLElement,
  ctnrSelector: string, nameSelector: string, chnlCountSelector: string
): number {
  return Array.from(thElem?.querySelectorAll(ctnrSelector))
    .reduce((maxDim, container) => {
      const pkgTextDim =
        (container.querySelector(nameSelector) as HTMLElement)
          ?.getBoundingClientRect();
      const pkgChnlCountDim =
        (container.querySelector(chnlCountSelector) as HTMLElement)
          ?.getBoundingClientRect();

      const pkgMaxDim = Math.max(
        pkgTextDim?.width,
        pkgTextDim?.height,
        pkgChnlCountDim?.width,
        pkgChnlCountDim?.height
      );
      return Math.max(maxDim, pkgMaxDim);
    }, -1);
};

export function getLargestMultipleOf(num: number, multiple: number): number {
  if (num <= 0 || multiple <= 0) {
    return -1;
  }
  const remainder = num % multiple;
  return remainder === 0 ? num : num - remainder;
}

// Indicator priority when the same channel/package slot disagrees across duplicate
// entries — IN (fully included) beats PROMO (introductory) beats ADDON (paid add-on).
// Typed as Record<PACKAGE_INDICATORS, number> so a missing member is a local
// `tsc --noEmit` error — that is NOT a guaranteed CI/build failure: next.config.js
// sets `typescript.ignoreBuildErrors: true` and the production build does not
// typecheck src/. The actual runtime enforcement for a value outside this map is
// rankIndicator's own-property check + warn below (there is no `?? 0` involved).
const INDICATOR_PRIORITY: Record<PACKAGE_INDICATORS, number> = {
  [PACKAGE_INDICATORS.IN]: 3,
  [PACKAGE_INDICATORS.PROMO]: 2,
  [PACKAGE_INDICATORS.ADDON]: 1
};

/** A single package indicator, or `null`/`undefined` for "no data". */
export type IndicatorValue = PACKAGE_INDICATORS | null | undefined;

function isRecognizedIndicator(value: IndicatorValue): boolean {
  return value == null || Object.prototype.hasOwnProperty.call(INDICATOR_PRIORITY, value);
}

/**
 * @description [WAT-11256] Ranks a single package indicator for merge comparison —
 * higher wins. `null`/`undefined` (no data) AND an unrecognized value both rank at
 * -1 — i.e. an unrecognized value never outranks real "no data"; the merge's final
 * normalization step (see mergeIndicators) is what stops it winning a comparison
 * against another unrecognized value and reaching the DOM.
 *
 * HAZARD: `PACKAGE_INDICATORS.ADDON === 0`, so this is a `== null` check, never a
 * falsiness check (`!value`) — that would treat every real ADDON indicator as "no
 * data" and silently erase it.
 *
 * Uses `Object.prototype.hasOwnProperty.call`, NOT the `in` operator — `in` walks
 * the prototype chain, so `"constructor"`/`"toString"`/`"valueOf"`/`"__proto__"`/
 * `"hasOwnProperty"` all resolve to an inherited `Object.prototype` member and skip
 * the warn entirely, carrying that value through as if it were legitimate.
 *
 * Warns on an unrecognized value rather than ranking it last in silence — see the
 * INDICATOR_PRIORITY comment for why this warn is the actual enforcement boundary
 * for a future 4th provider indicator in prod (the type annotation is not).
 */
function rankIndicator(value: IndicatorValue): number {
  if (value == null) {
    return -1;
  }
  if (!isRecognizedIndicator(value)) {
    console.warn("[PackagesGrid] rankIndicator: unrecognized package indicator value, ranking it last", value);
    return -1;
  }
  return INDICATOR_PRIORITY[value];
}

/**
 * @description [WAT-11256] Merges two per-package indicator arrays for the same
 * consolidated channel, keeping the higher-ranked value at each index (rankIndicator).
 * Arrays may be different lengths — the reachable case is a feed channel whose own
 * `indicators` array is shorter than the current package count (index-filtered, same
 * as any INCLUDED/ADD_ON entry) — this is NOT a property of the MyFree bucket; MyFree
 * exclusion is a decision the CALLER makes via `excludeKey` (see
 * consolidateSearchChannels), not something this function knows or enforces.
 * `Math.max` (not `Math.min`) so the longer array's tail slots are preserved rather
 * than truncated.
 *
 * Normalizes the winning value at each index back to `null` if it isn't a real,
 * recognized indicator — otherwise two unrecognized values (equal rank, -1 each)
 * would "win" the comparison and an unrecognized raw value would reach
 * `ChannelRow.tsx`, which only skips a cell on a strict `!== null` check.
 */
function mergeIndicators(a?: IndicatorValue[], b?: IndicatorValue[]): IndicatorValue[] {
  const len = Math.max(a?.length ?? 0, b?.length ?? 0);
  const merged: IndicatorValue[] = [];
  for (let ix = 0; ix < len; ix += 1) {
    const av = a?.[ix];
    const bv = b?.[ix];
    const winner = rankIndicator(bv) > rankIndicator(av) ? bv : av;
    merged.push(isRecognizedIndicator(winner) ? (winner ?? null) : null);
  }
  return merged;
}

/**
 * @description [WAT-11256] A single consolidated search result row. `indicators` is
 * freshly merged and authoritative; every OTHER field (`type`, `category`, `rsntier`,
 * logo/`isOvLogo`, …) is a snapshot of whichever source bucket entry
 * `consolidateSearchChannels` encountered first for that identity — later duplicates
 * only ever contribute their `indicators`. Don't trust `type`/`category` on a
 * consolidated row to describe every package it's now shown against; the grid itself
 * doesn't — see `isAddOnOnlyRow` in PackageCmprGrid.tsx, which derives styling from
 * merged `indicators`, not from this stale `type`. `chnlNum` is declared explicitly
 * (not folded into the index signature) because it is half of the dedupe identity
 * and half of the rewritten `uuid` below.
 */
export interface IConsolidatedSearchChannel {
  uuid: string;
  name: string;
  chnlNum?: string;
  indicators: IndicatorValue[];
  [key: string]: unknown;
}

/**
 * @description [WAT-11256 round-6 S12] The untrusted per-bucket row shape a
 * `channelsAZ` bucket entry is expected to have. Every field is feed-supplied and is
 * verified by consolidateSearchChannels' own runtime guards below — this annotation
 * names the fields the function reads, it does not enforce them (`Array.isArray`
 * narrows `unknown` to `any[]`, so without this cast every `channel` in the loop is
 * `any` again regardless of `channelsAZ`'s own tightened type).
 */
interface ISourceChannel {
  name?: string;
  chnlNum?: string;
  indicators?: IndicatorValue[];
  [key: string]: unknown;
}

/**
 * @description [WAT-11256] Consolidates duplicate channel search results.
 * The same channel can appear more than once across the AZ grid's category buckets
 * (e.g. INCLUDED_CHANNELS + ADD_ON_CHANNELS) when it's included in some packages and
 * an add-on/promo in others. During an active search, those duplicates must collapse
 * into a single row per channel with indicators merged across every occurrence so no
 * package-availability signal is lost.
 *
 * Precondition: `channelsAZ` must already be search-filtered (e.g. the grid's
 * `filteredGridChannelsAZ`) — this function does not filter by the search term itself.
 *
 * What actually merges vs. what doesn't: only `indicators` are merged across
 * duplicates — see IConsolidatedSearchChannel. `uuid` is rewritten to
 * `Consolidated-<name>-<chnlNum>`, which changes the DOM id ChannelRow derives from
 * it during search.
 *
 * Identity is name + channel number, not name alone (display names are not unique —
 * locals/aliases — and this is the same pair the store's own uuids use, e.g.
 * `AddOn-${name}-${chnlNum}`). A channel whose `name` is missing or not a string is
 * skipped (and warned on) rather than merged — that check is what keeps THIS
 * function's own `channel.name.toLowerCase()` (below) from throwing on a malformed
 * feed row; the comparator this list is later sorted with is unguarded too, but is
 * never reached with a bad value because the skip happens first.
 *
 * Callers should exclude the MyFree DIRECTV bucket via `excludeKey` — its entries
 * are synthetic all-IN indicators (every package column) and are only truthful next
 * to the MyFree label + satellite caveat, which this flat consolidation drops. This
 * function does NOT decide what happens to an excluded bucket's matches — a caller
 * that excludes MyFree and then treats "0 consolidated rows" as "no results" will
 * hide a real MyFree match; the caller must count/render the excluded bucket itself.
 *
 * @param channelsAZ - the already search-filtered AZ-grouped channel buckets; every
 *   value is expected to be an array, but a malformed/non-array bucket is skipped
 *   (and warned on) rather than thrown on, matching the rest of this function's
 *   defensive style. Optional because `channelsAZ` can genuinely be `undefined`
 *   mid-fetch, before the store has populated it.
 * @param excludeKey - a single bucket key to skip entirely (e.g. the MyFree category
 *   key — routinely `undefined` when that key is CMS-unauthored, which is why this
 *   accepts `string | undefined` rather than pretending that can't happen)
 * @returns a single, alphabetically-sorted, deduplicated channel list
 */
export function consolidateSearchChannels(
  channelsAZ?: Record<string, unknown>,
  excludeKey?: string
): IConsolidatedSearchChannel[] {
  const consolidated = new Map<string, IConsolidatedSearchChannel>();

  for (const [key, bucket] of Object.entries(channelsAZ || {})) {
    if (key === excludeKey) {
      continue;
    }
    if (!Array.isArray(bucket)) {
      console.warn("[PackagesGrid] consolidateSearchChannels: bucket skipped, not an array", key, bucket);
      continue;
    }
    // [WAT-11256 round-6 S12] `Array.isArray` is declared `(arg: any) => arg is
    // any[]`, so narrowing `unknown` through it launders the bucket straight back to
    // `any` — every `channel` below would silently be `any` again despite the
    // tightened `channelsAZ` param, which is how the `indicators` gap (S2 above)
    // survived the same edit that added guards for `name` and the bucket itself.
    // This cast documents the untrusted shape; it does not enforce it — the runtime
    // guards in this loop are what actually do.
    for (const channel of bucket as ISourceChannel[]) {
      if (typeof channel?.name !== "string" || !channel.name) {
        console.warn("[PackagesGrid] consolidateSearchChannels: channel skipped, name is missing or not a string", channel);
        continue;
      }
      // Identity is name + channel number — the same pair the store's own uuids use
      // (`AddOn-${name}-${chnlNum}`); display name alone is not unique (locals/aliases).
      const dedupeKey = `${channel.name.toLowerCase()}|${channel?.chnlNum ?? ""}`;
      const existing = consolidated.get(dedupeKey);
      // [WAT-11256 round-6 S2] Untrusted feed value — chlupStore passes
      // `channel?.indicators` straight through, so it may be absent or a truthy
      // non-iterable (which would throw `is not iterable` on a bare spread, and
      // escalate one malformed row into the whole-search error state via the
      // caller's catch). Guarded here so BOTH construction paths below — first
      // occurrence (seeded) and 2nd+ occurrence (merged) — establish the same
      // recognized-or-null invariant; before this, only the merge path normalized
      // an unrecognized value, so a channel appearing exactly once (the common case)
      // still carried a raw, unrecognized indicator into ChannelRow's `!== null` gate.
      const rawIndicators: IndicatorValue[] = Array.isArray(channel?.indicators) ? channel.indicators : [];
      if (!existing) {
        consolidated.set(dedupeKey, {
          ...channel,
          uuid: `Consolidated-${channel.name}-${channel?.chnlNum ?? ""}`,
          indicators: rawIndicators.map(value => (isRecognizedIndicator(value) ? (value ?? null) : null))
        });
      } else {
        existing.indicators = mergeIndicators(existing?.indicators, rawIndicators);
      }
    }
  }

  return Array.from(consolidated.values()).sort(Channels.sortAlphabetically);
}
